import React, { useContext, useEffect, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Paper,
    Grid,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tabs,
    Tab,
    Card,
    CardContent,
    Chip,
    InputAdornment,
    Alert,
    Stack
} from '@mui/material';
import { Line, Bar } from 'react-chartjs-2';
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from 'react-router-dom';
import InfoIcon from '@mui/icons-material/Info';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SellIcon from '@mui/icons-material/Sell';

import ApiFetch from './ApiFetch';
import { LoginInfo } from '../App';
import Header from './Header';
import Footer from './Footer';
import Category from './Category';

const Invest = () => {
    const { t } = useTranslation();
    const { loginInfo } = useContext(LoginInfo);
    const location = useLocation();
    const params = useParams();

    // State declarations
    const [registrationList, setRegistrationList] = useState(null);
    const [currentValue, setCurrentValue] = useState(0);
    const [yesterdayValue, setYesterdayValue] = useState(0);
    const [hikaku, setHikaku] = useState(0);
    const [hikakuStr, setHikakuStr] = useState('0');
    const [per, setPer] = useState(0);
    const [perStr, setPerStr] = useState('0');
    const [investList, setInvestList] = useState([]);
    const [avg, setAvg] = useState(0);
    const [count, setCount] = useState(1);
    const [pointOrPrice, setPointOrPrice] = useState(0);
    const [point, setPoint] = useState(0);
    const [marketCap, setMarketCap] = useState(0);
    const [totalNumberOfUnits, setTotalNumberOfUnits] = useState(0);
    const [transaction, setTransaction] = useState(null);
    const [transactionTotal, setTransactionTotal] = useState(0);
    const [holderListTop10, setHolderListTop10] = useState([]);
    const [holders, setHolders] = useState(0);
    const [stockCode, setStockCode] = useState(params.stock_code || "1000");
    const [stockName, setStockName] = useState("");

    // Dialog state
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogTitle, setDialogTitle] = useState("");
    const [dialogMessage, setDialogMessage] = useState("");
    const [dialogType, setDialogType] = useState("info"); // info, buy, sell

    // Tab state
    const [tabKey, setTabKey] = useState("top10");

    // Chart options
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                text: '価格推移'
            }
        }
    };

    const barChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
            }
        },
        scales: {
            y: {
                beginAtZero: true
            }
        }
    };
    // Chart data creation functions
    function createGraphData(r, title) {
        return {
            labels: r.map(d => [d.ymd]),
            datasets: [{
                data: r.map(d => d.value),
                label: title,
                borderColor: '#1976d2',
                backgroundColor: 'rgba(25, 118, 210, 0.1)',
                tension: 0.1,
                fill: true
            }]
        };
    }

    function createTransactionGraphData(r) {
        return {
            labels: r.map(d => [d.time]),
            datasets: [
                {
                    data: r.map(d => d.type2),
                    label: "売",
                    backgroundColor: '#ef5350',
                },
                {
                    data: r.map(d => d.type1),
                    label: "買",
                    backgroundColor: '#4caf50',
                }
            ]
        };
    }

    async function getRegistrationByDay(code) {
        const response = await new ApiFetch().get(`${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/invest/data/${code}`);
        return response.json();
    }

    async function getInvestList() {
        const response = await new ApiFetch().get(`${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/invest/list`);
        return response.json();
    }

    async function getTransaction() {
        const response = await new ApiFetch().get(`${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/invest/transaction`);
        return response.json();
    }

    async function getHolderListTop10(code) {
        const response = await new ApiFetch().get(`${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/invest/top10/${code}`);
        return response.json();
    }

    async function getPoint() {
        const response = await new ApiFetch().get(`${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/point`);
        return response.json();
    }

    async function buy(code, amount) {
        const response = await new ApiFetch().put(`${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/invest/buy`, {
            stockCode: code,
            count: amount
        });
        return response.json();
    }

    async function sell(code, amount) {
        const response = await new ApiFetch().put(`${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/invest/sell`, {
            stockCode: code,
            count: amount
        });
        return response.json();
    }

    // Data update function
    async function updateData() {
        try {
            const [regData, invList, transData, pointData, holderData] = await Promise.all([
                getRegistrationByDay(stockCode),
                getInvestList(),
                getTransaction(),
                getPoint(),
                getHolderListTop10(stockCode)
            ]);

            if (regData.result) {
                const chartData = createGraphData(regData.data.list.reverse(), "価格推移");
                setRegistrationList(<Line data={chartData} options={chartOptions} />);

                setCurrentValue(regData.data.today_value);
                setYesterdayValue(regData.data.yesterday_value);
                setTotalNumberOfUnits(regData.data.all_count);
                setMarketCap(regData.data.today_value * regData.data.total_count);

                const hikakuValue = regData.data.today_value - regData.data.yesterday_value;
                const perValue = (hikakuValue / regData.data.yesterday_value * 100).toFixed(2);

                setPer(perValue);
                setPerStr(perValue > 0 ? `+${perValue}` : perValue);
                setHikaku(hikakuValue);
                setHikakuStr(hikakuValue > 0 ? `+${hikakuValue.toLocaleString()}` : hikakuValue.toLocaleString());
                setStockName(regData.data.stock_name);
            }

            if (invList.result) {
                setInvestList(invList.data);
                if (invList.data.length > 0) {
                    setAvg(parseFloat(invList.data[0].avg));
                }
            }

            if (transData.result) {
                const total = transData.data.list.reduce((acc, curr) => acc + curr.type1 + curr.type2, 0);
                setTransactionTotal(total);
                const chartData = createTransactionGraphData(transData.data.list);
                setTransaction(<Bar data={chartData} options={barChartOptions} />);
            }

            if (pointData.result) {
                setPoint(pointData.point);
            }

            if (holderData.result && holderData.data.list) {
                setHolderListTop10(holderData.data.list);
                setHolders(holderData.data.holders);
            }
        } catch (error) {
            handleDialog("エラー", "データの更新に失敗しました");
        }
    }
    // Dialog handlers
    const handleDialog = (title, message, type = "info") => {
        setDialogTitle(title);
        setDialogMessage(message);
        setDialogType(type);
        setDialogOpen(true);
    };

    const [buyDialogOpen, setBuyDialogOpen] = useState(false);
    const [sellDialogOpen, setSellDialogOpen] = useState(false);
    const [selectedStock, setSelectedStock] = useState(null);

    const handleBuyClick = (stock) => {
        setSelectedStock(stock);
        setBuyDialogOpen(true);
    };

    const handleSellClick = (stock) => {
        setSelectedStock(stock);
        setSellDialogOpen(true);
    };

    const handleBuyConfirm = async (count) => {
        try {
            const result = await buy(stockCode, count);
            if (result.result) {
                setBuyDialogOpen(false);
                handleDialog(t("common.info"),
                    <Alert severity="success" icon={<CheckCircleIcon />}>
                        購入が完了しました
                    </Alert>
                );
                await updateData();
            } else {
                handleDialog("エラー",
                    <Alert severity="error">
                        {result.errors}
                    </Alert>
                );
            }
        } catch (error) {
            handleDialog("エラー",
                <Alert severity="error">
                    取引に失敗しました
                </Alert>
            );
        }
    };

    const handleSellConfirm = async (count) => {
        try {
            const result = await sell(stockCode, count);
            if (result.result) {
                setSellDialogOpen(false);
                handleDialog(t("common.info"),
                    <Alert severity="success" icon={<CheckCircleIcon />}>
                        売却が完了しました
                    </Alert>
                );
                await updateData();
            } else {
                handleDialog("エラー",
                    <Alert severity="error">
                        {result.errors}
                    </Alert>
                );
            }
        } catch (error) {
            handleDialog("エラー",
                <Alert severity="error">
                    取引に失敗しました
                </Alert>
            );
        }
    };

    // Effect hooks
    useEffect(() => {
        document.title = "ポイント投資 - ourt AI";
        updateData();
    }, []);

    useEffect(() => {
        setPointOrPrice(currentValue * count);
    }, [count, currentValue]);

    const BuyDialog = ({ open, onClose, stock, currentValue, onConfirm }) => {
        const [count, setCount] = useState(1);
        const amount = Math.ceil(currentValue * count * 1.05);

        return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)'
                    }
                }}
            >
                <DialogTitle sx={{ 
                    background: 'linear-gradient(135deg, #f44336 0%, #ef5350 100%)',
                    color: 'white',
                    m: 0
                }}>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                        🛒 買い注文
                    </Typography>
                </DialogTitle>

                <DialogContent sx={{ p: 3 }}>
                    <Box>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                銘柄: {stock?.stock_name}
                            </Typography>
                        </Alert>
                        
                        <Card sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                            <CardContent>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={4}>
                                        <Typography variant="body2" color="text.secondary">
                                            現在値
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                            {currentValue?.toLocaleString()}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography variant="body2" color="text.secondary">
                                            購入数量
                                        </Typography>
                                        <TextField
                                            type="number"
                                            value={count}
                                            onChange={(e) => {
                                                const value = parseInt(e.target.value) || 0;
                                                setCount(Math.max(1, Math.min(1000000, value)));
                                            }}
                                            inputProps={{ min: 1, max: 1000000 }}
                                            size="small"
                                            fullWidth
                                            InputProps={{
                                                endAdornment: <InputAdornment position="end">株</InputAdornment>
                                            }}
                                            sx={{ mt: 0.5 }}
                                        />
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography variant="body2" color="text.secondary">
                                            必要ポイント
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                                            {amount.toLocaleString()}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            (手数料5%込)
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 3, pt: 0 }}>
                    <Button 
                        onClick={onClose} 
                        color="inherit"
                        variant="outlined"
                        sx={{ borderRadius: 2 }}
                    >
                        キャンセル
                    </Button>
                    <Button
                        onClick={() => onConfirm(count)}
                        variant="contained"
                        color="error"
                        startIcon={<ShoppingCartIcon />}
                        sx={{ borderRadius: 2, minWidth: 120 }}
                    >
                        購入
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    const SellDialog = ({ open, onClose, stock, currentValue, avg, onConfirm }) => {
        const [count, setCount] = useState(1);
        const amount = (() => {
            const totalValue = currentValue * count;
            const taxAmount = currentValue > avg ?
                Math.ceil(((currentValue * count) - (avg * count)) * 0.20315) : 0;
            return totalValue - taxAmount;
        })();

        return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)'
                    }
                }}
            >
                <DialogTitle sx={{ 
                    background: 'linear-gradient(135deg, #2196f3 0%, #21cbf3 100%)',
                    color: 'white',
                    m: 0
                }}>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                        💰 売り注文
                    </Typography>
                </DialogTitle>

                <DialogContent sx={{ p: 3 }}>
                    <Box>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                銘柄: {stock?.stock_name}
                            </Typography>
                        </Alert>
                        
                        <Card sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                            <CardContent>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={4}>
                                        <Typography variant="body2" color="text.secondary">
                                            現在値
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                            {currentValue?.toLocaleString()}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography variant="body2" color="text.secondary">
                                            売却数量
                                        </Typography>
                                        <TextField
                                            type="number"
                                            value={count}
                                            onChange={(e) => {
                                                const value = parseInt(e.target.value) || 0;
                                                setCount(Math.max(1, Math.min(stock?.count || 1000000, value)));
                                            }}
                                            inputProps={{ min: 1, max: stock?.count }}
                                            size="small"
                                            fullWidth
                                            InputProps={{
                                                endAdornment: <InputAdornment position="end">株</InputAdornment>
                                            }}
                                            sx={{ mt: 0.5 }}
                                        />
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography variant="body2" color="text.secondary">
                                            売却ポイント
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                            {amount.toLocaleString()}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            (源泉徴収税込)
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 3, pt: 0 }}>
                    <Button 
                        onClick={onClose} 
                        color="inherit"
                        variant="outlined"
                        sx={{ borderRadius: 2 }}
                    >
                        キャンセル
                    </Button>
                    <Button
                        onClick={() => onConfirm(count)}
                        variant="contained"
                        color="primary"
                        startIcon={<SellIcon />}
                        sx={{ borderRadius: 2, minWidth: 120 }}
                    >
                        売却
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    return (
        <Box className="wrapper" sx={{ 
            minHeight: '100vh',
            backgroundColor: '#f8f9fa'
        }}>
            <Header />
            <Category />

            <Container maxWidth="lg" sx={{ 
                py: { xs: 2, sm: 4 },
                px: { xs: 1, sm: 2 }
            }}>
                {/* Header Section */}
                <Paper sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    mb: 4, 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    borderRadius: 3
                }}>
                    <Typography variant="h4" gutterBottom sx={{
                        borderBottom: '3px solid rgba(255,255,255,0.3)',
                        pb: 1,
                        fontWeight: 'bold'
                    }}>
                        ポイント運用
                    </Typography>

                    <Typography variant="body1" paragraph sx={{ opacity: 0.9 }}>
                        「ポイント運用」は株取引シミュレーションゲームです。
                    </Typography>

                    <Button
                        variant="contained"
                        sx={{ 
                            backgroundColor: 'rgba(255,255,255,0.2)', 
                            color: 'white',
                            '&:hover': {
                                backgroundColor: 'rgba(255,255,255,0.3)'
                            }
                        }}
                        startIcon={<InfoIcon />}
                        onClick={() => handleDialog("ポイント運用とは",
                            <Box>
                                <Typography paragraph>
                                    ポイント運用機能は、ポイントを利用して投資シミュレーションし、
                                    その変動に応じてポイントが増減するサービスです。
                                </Typography>
                                <Typography paragraph>
                                    アワートAI全体の活動状況によって価格が変動し、その変動によって運用しているポイントが変化します。
                                </Typography>
                                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                    使い方
                                </Typography>
                                <Typography paragraph>
                                    「買」ボタンを押して購入したい株数だけ購入します。
                                    基準価格がこのとき10だとして、20になったときに「売」ボタンから売却すると、
                                    買った時より10ポイント多く戻ってきます。このようにしてうまく増やしていきましょう。
                                </Typography>
                            </Box>
                        )}
                    >
                        ポイント運用の説明
                    </Button>
                </Paper>

                {/* Stock Information Section */}
                <Card sx={{ 
                    mb: 4, 
                    overflow: 'visible',
                    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                }}>
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 2,
                                    flexWrap: 'wrap'
                                }}>
                                    <Chip 
                                        label={stockCode} 
                                        color="primary" 
                                        variant="filled"
                                        sx={{ fontWeight: 'bold' }}
                                    />
                                    <Typography 
                                        variant="h5" 
                                        component="div" 
                                        sx={{ 
                                            fontWeight: 'bold',
                                            color: 'text.primary'
                                        }}
                                    >
                                        {stockName}
                                    </Typography>
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Card sx={{ 
                                    background: 'rgba(255,255,255,0.8)', 
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255,255,255,0.2)'
                                }}>
                                    <CardContent>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                            現在値
                                        </Typography>
                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                            <Typography 
                                                variant="h3" 
                                                component="div" 
                                                sx={{ 
                                                    fontWeight: 'bold',
                                                    color: 'text.primary'
                                                }}
                                            >
                                                {currentValue.toLocaleString()}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                ポイント
                                            </Typography>
                                        </Stack>
                                        
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: 1,
                                            p: 1,
                                            borderRadius: 2,
                                            backgroundColor: hikaku >= 0 ? 'error.light' : 'primary.light',
                                            color: hikaku >= 0 ? 'error.contrastText' : 'primary.contrastText'
                                        }}>
                                            {hikaku >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                {hikakuStr}
                                            </Typography>
                                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                ({perStr}%)
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Card sx={{ 
                                    background: 'rgba(255,255,255,0.8)', 
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255,255,255,0.2)'
                                }}>
                                    <CardContent>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                            市場情報
                                        </Typography>
                                        <Stack spacing={2}>
                                            <Box>
                                                <Typography variant="body2" color="text.secondary">
                                                    時価総額
                                                </Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                    {marketCap.toLocaleString()} ポイント
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" color="text.secondary">
                                                    発行済株式総数
                                                </Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                    {totalNumberOfUnits.toLocaleString()}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Chart Section */}
                            <Grid item xs={12}>
                                <Card sx={{ 
                                    background: 'rgba(255,255,255,0.9)', 
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255,255,255,0.2)'
                                }}>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                            📈 価格チャート
                                        </Typography>
                                        <Box sx={{ height: 400, mt: 2 }}>
                                            {registrationList}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* Transaction Volume Chart */}
                <Card sx={{ 
                    mb: 4, 
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                }}>
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                            時間帯別取引数量(前日)
                        </Typography>
                        <Chip 
                            label={`取引額合計：${(transactionTotal * currentValue).toLocaleString()} ポイント`}
                            color="primary"
                            variant="outlined"
                            sx={{ mb: 2, fontWeight: 'bold' }}
                        />
                        <Card sx={{ 
                            background: 'rgba(255,255,255,0.9)', 
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255,255,255,0.2)'
                        }}>
                            <CardContent>
                                <Box sx={{ height: 300 }}>
                                    {transaction}
                                </Box>
                            </CardContent>
                        </Card>
                    </CardContent>
                </Card>

                {/* Points and Holdings Section */}
                <Grid container spacing={4}>
                    {/* Points Card */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ 
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            borderRadius: 3,
                            boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
                        }}>
                            <CardContent sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="h6" gutterBottom sx={{ opacity: 0.9 }}>
                                    💰 保有ポイント
                                </Typography>
                                <Typography variant="h2" sx={{ 
                                    fontWeight: 'bold',
                                    textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                    mb: 1
                                }}>
                                    {point.toLocaleString()}
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                    ポイント
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Quick Actions Card */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ 
                            background: 'linear-gradient(135deg, #2196f3 0%, #21cbf3 100%)',
                            color: 'white',
                            borderRadius: 3,
                            boxShadow: '0 8px 32px rgba(33, 150, 243, 0.3)'
                        }}>
                            <CardContent sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="h6" gutterBottom sx={{ opacity: 0.9 }}>
                                    📈 クイックアクション
                                </Typography>
                                <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2 }}>
                                    <Button
                                        variant="contained"
                                        sx={{ 
                                            backgroundColor: 'rgba(255,255,255,0.2)', 
                                            color: 'white',
                                            '&:hover': {
                                                backgroundColor: 'rgba(255,255,255,0.3)'
                                            }
                                        }}
                                        startIcon={<ShoppingCartIcon />}
                                        onClick={() => handleBuyClick({ stock_name: stockName })}
                                    >
                                        購入
                                    </Button>
                                    <Button
                                        variant="contained"
                                        sx={{ 
                                            backgroundColor: 'rgba(255,255,255,0.2)', 
                                            color: 'white',
                                            '&:hover': {
                                                backgroundColor: 'rgba(255,255,255,0.3)'
                                            }
                                        }}
                                        startIcon={<SellIcon />}
                                        onClick={() => investList.length > 0 && handleSellClick(investList[0])}
                                        disabled={investList.length === 0}
                                    >
                                        売却
                                    </Button>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Holdings Section */}
                    <Grid item xs={12}>
                        <Typography variant="h5" gutterBottom sx={{ 
                            mt: 2, 
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}>
                            📊 保有銘柄
                        </Typography>
                        
                        {investList.length === 0 ? (
                            <Card sx={{ 
                                p: 4, 
                                textAlign: 'center',
                                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                                borderRadius: 3
                            }}>
                                <Typography variant="h6" color="text.secondary" gutterBottom>
                                    保有銘柄がありません
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    まずは株式を購入してみましょう
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<ShoppingCartIcon />}
                                    onClick={() => handleBuyClick({ stock_name: stockName })}
                                >
                                    購入する
                                </Button>
                            </Card>
                        ) : (
                            <Grid container spacing={3}>
                                {investList.map((d, i) => {
                                    const valuationGainOrLoss = Math.floor(currentValue * d.count - d.avg * d.count);
                                    const isGain = valuationGainOrLoss >= 0;
                                    const formattedGainLoss = isGain
                                        ? `+${valuationGainOrLoss.toLocaleString()}`
                                        : valuationGainOrLoss.toLocaleString();
                                    const gainLossPercentage = ((currentValue - d.avg) / d.avg * 100).toFixed(2);

                                    return (
                                        <Grid item xs={12} key={i}>
                                            <Card sx={{ 
                                                borderRadius: 3,
                                                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                                overflow: 'hidden',
                                                border: isGain ? '2px solid #4caf50' : '2px solid #f44336'
                                            }}>
                                                <CardContent sx={{ p: 0 }}>
                                                    {/* Header */}
                                                    <Box sx={{ 
                                                        p: 2, 
                                                        background: isGain 
                                                            ? 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)'
                                                            : 'linear-gradient(135deg, #f44336 0%, #ef5350 100%)',
                                                        color: 'white'
                                                    }}>
                                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                            {d.stock_name}
                                                        </Typography>
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                                                {formattedGainLoss}
                                                            </Typography>
                                                            <Chip 
                                                                label={`${isGain ? '+' : ''}${gainLossPercentage}%`}
                                                                size="small"
                                                                sx={{ 
                                                                    backgroundColor: 'rgba(255,255,255,0.2)',
                                                                    color: 'white',
                                                                    fontWeight: 'bold'
                                                                }}
                                                            />
                                                        </Stack>
                                                    </Box>
                                                    
                                                    {/* Details */}
                                                    <Grid container sx={{ p: 2 }}>
                                                        <Grid item xs={6} md={3}>
                                                            <Typography variant="body2" color="text.secondary">
                                                                平均取得単価
                                                            </Typography>
                                                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                                {d.avg.toLocaleString()}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={6} md={3}>
                                                            <Typography variant="body2" color="text.secondary">
                                                                現在値
                                                            </Typography>
                                                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                                {currentValue.toLocaleString()}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={6} md={3}>
                                                            <Typography variant="body2" color="text.secondary">
                                                                保有数量
                                                            </Typography>
                                                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                                {d.count.toLocaleString()}株
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={6} md={3}>
                                                            <Typography variant="body2" color="text.secondary">
                                                                時価評価額
                                                            </Typography>
                                                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                                {(d.count * currentValue).toLocaleString()}
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                    
                                                    {/* Action Buttons */}
                                                    <Box sx={{ 
                                                        p: 2, 
                                                        backgroundColor: 'grey.50',
                                                        display: 'flex',
                                                        gap: 2,
                                                        justifyContent: 'center'
                                                    }}>
                                                        <Button
                                                            variant="contained"
                                                            color="error"
                                                            startIcon={<ShoppingCartIcon />}
                                                            onClick={() => handleBuyClick(d)}
                                                            sx={{ minWidth: 100 }}
                                                        >
                                                            買い増し
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            startIcon={<SellIcon />}
                                                            onClick={() => handleSellClick(d)}
                                                            sx={{ minWidth: 100 }}
                                                        >
                                                            売却
                                                        </Button>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        )}
                    </Grid>
                </Grid>

                {/* Spacing for better layout */}
                <Box sx={{ mb: 4 }} />
                {/* Shareholders Section */}
                <Card sx={{ 
                    mb: 4,
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
                }}>
                    <CardContent sx={{ p: 0 }}>
                        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
                            <Typography variant="h5" sx={{ 
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}>
                                👥 株主情報
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                総株主数: {holders}名
                            </Typography>
                        </Box>

                        <Grid container spacing={2} sx={{ p: 3 }}>
                            {holderListTop10.map((d, index) => (
                                <Grid item xs={12} md={6} key={index}>
                                    <Card sx={{ 
                                        background: 'rgba(255,255,255,0.9)',
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        borderRadius: 2
                                    }}>
                                        <CardContent sx={{ p: 2 }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Box>
                                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                        #{index + 1} {d.user_name}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {d.count.toLocaleString()} 株保有
                                                    </Typography>
                                                </Box>
                                                <Chip
                                                    label={`${d.per}%`}
                                                    color="primary"
                                                    variant="filled"
                                                    sx={{ 
                                                        fontWeight: 'bold',
                                                        minWidth: 60
                                                    }}
                                                />
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </CardContent>
                </Card>
            </Container>
            <Footer />

            {/* ダイアログ部分 */}
            <BuyDialog
                open={buyDialogOpen}
                onClose={() => setBuyDialogOpen(false)}
                stock={selectedStock}
                currentValue={currentValue}
                onConfirm={handleBuyConfirm}
            />

            <SellDialog
                open={sellDialogOpen}
                onClose={() => setSellDialogOpen(false)}
                stock={selectedStock}
                currentValue={currentValue}
                avg={avg}
                onConfirm={handleSellConfirm}
            />

            {/* 通知用ダイアログ */}
            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)'
                    }
                }}
            >
                <DialogTitle sx={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    m: 0
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {dialogTitle}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                    {dialogMessage}
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 0 }}>
                    <Button 
                        onClick={() => setDialogOpen(false)}
                        variant="contained"
                        sx={{ borderRadius: 2, minWidth: 100 }}
                    >
                        閉じる
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Invest;